import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import {
    CircularProgress, Grid, Paper, Stack,
    styled, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, useMediaQuery
} from '@mui/material';
import { useState } from 'react';
import { observer } from 'mobx-react-lite';
import agent from '../../../../app/api/agent';
import { DoughnutChart } from '../../../../app/models/charts';
import { KoDataErasureReport } from '../../../../app/models/dataErasure';
import { useStore } from '../../../../app/stores/store';
import KoReportingChart from '../koReportingCharts/KoReportingChart';
import CloseIcon from '@mui/icons-material/Close';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';

const ItemChart = styled(Paper)(({ theme }) => ({
    backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
    ...theme.typography.body2,
    padding: theme.spacing(1),
    color: theme.palette.text.secondary,
    marginTop: 4,
    '& canvas': {
        margin: '0 auto',
        width: 300,
    }
}));

const style = (isMobile: boolean) => {
    return ({
        position: 'absolute' as 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: isMobile ? 700 : 360,
        bgcolor: '#DAE5F1',
        border: '2px solid #000',
        boxShadow: 24,
        p: 2,
    })
};
const tableCellStyle = (matches: boolean) => {
    return {
        padding: matches ? 2 : 1,
        lineHeight: 1.6,

    }
}
function createData1(name1: string) {
    return { name1 };
}
const leftColumn = [
    createData1('HW Value:XXXX'),
    createData1('Service Fee: XXXXXX'),
    createData1('Residual Value: XXXXXX'),
]
type Props = {
    reportData: KoDataErasureReport;
    totalReceived: number;
    totalUpcycle: number;
    totalRecycle: number;
}
const getDataCaringUnitData = (chartval: number[], chartLabel: string[]) => {
    const data: DoughnutChart = {
        labels: chartLabel,
        datasets: [
            {
                label: 'Any Name',
                data: chartval,
                backgroundColor: [
                    'rgb(68,114,196)',
                    'rgb(237,125,49)',
                ],
                borderColor: [
                    'rgb(68,114,196)',
                    'rgb(237,125,49)',
                ],
                borderWidth: 1,
            },
        ],
    };
    return data;
}

interface koReportingModelTable {
    Name: string;
    Received: number;
    Upcycled: number;
    Recycled: number;
}
const koReportingTableHead = [
    "Type",
    "Received",
    "Upcycled",
    "Recycled",
]

const KoModal = ({ reportData, totalReceived, totalUpcycle, totalRecycle }: Props) => {
    const [excelLoading, setExcelLoading] = useState(false);
    const { userStore, modalStore } = useStore();
    const { closeModal } = modalStore;
    const { isT1aAdmin } = userStore;

    const matches = useMediaQuery('(min-width:900px)');

    if (!reportData) {
        return null;
    }
    const totalHandler = () => {
        return reportData.received.laptops + reportData.received.desktops + reportData.received.tft;
    }
    const percentageHandler = (isUpcycled: Boolean, isRecycled: Boolean) => {
        const totalReceived = totalHandler();
        const erasedTotal = reportData.erased.laptops + reportData.erased.desktops + reportData.erased.tft;
        const defectiveTotal = reportData.defective.laptops + reportData.defective.desktops + reportData.defective.tft;
        if (isUpcycled && erasedTotal > 0) {
            return +((erasedTotal / (totalReceived)) * 100).toFixed(2);
        }
        if (isRecycled && defectiveTotal > 0) {
            return +((defectiveTotal / (totalReceived)) * 100).toFixed(2);
        }
        return +0;
    }
    const downloadTemplate = (pickupNumber: string) => {
        if (pickupNumber) {
            setExcelLoading(true);
            try {
                agent.KoOverview.downloadKoDataErasureReportToExcel(pickupNumber)
                    .then((res) => {
                        const url = window.URL.createObjectURL(new Blob([res.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', 'KoDataReport.xlsx');
                        document.body.appendChild(link);
                        link.click();
                        setExcelLoading(false);
                    })
                    .catch((error) => setExcelLoading(false));
            } catch (error) {
                setExcelLoading(false);
            }
        }
    }

    const koReportingTableRows: koReportingModelTable[] = [

        {
            Name: 'Laptops',
            Received: reportData.received.laptops,
            Upcycled: reportData.erased.laptops,
            Recycled: reportData.defective.laptops,
        },
        {
            Name: 'Desktops',
            Received: reportData.received.desktops,
            Upcycled: reportData.erased.desktops,
            Recycled: reportData.defective.desktops,
        },
        {
            Name: 'TFT',
            Received: reportData.received.tft,
            Upcycled: reportData.erased.tft,
            Recycled: reportData.defective.tft,
        },
        {
            Name: 'Total(%)',
            Received: totalReceived,
            Upcycled: totalUpcycle,
            Recycled: totalRecycle,
        }
    ];

    const generateKoReportingTableHeading = () => {
        return (
            <TableRow key='tableHeadRow' sx={{ bgcolor: '#002352' }}>
                {koReportingTableHead.map((th) => (
                    <TableCell key={th} align="left" sx={{ padding: 1, lineHeight: 1.6, color: '#ffffff' }}>
                        {th}
                    </TableCell>
                ))}
            </TableRow>
        )
    };

    const generateKoReportingTableRows = () => {
        return (
            koReportingTableRows.map((tr) => {
                return (
                    <TableRow key={tr.Name} sx={{ '&:last-child td, &:last-child th': { border: 0 }, bgcolor: '#DAE5F1' }}>
                        <TableCell scope="row" sx={tableCellStyle(matches)}>
                            {tr.Name}
                        </TableCell>
                        <TableCell scope="row" sx={tableCellStyle(matches)}>
                            {tr.Received}
                        </TableCell>
                        <TableCell scope="row" sx={tableCellStyle(matches)}>
                            {tr.Upcycled}
                        </TableCell>
                        <TableCell scope="row" sx={tableCellStyle(matches)}>
                            {tr.Recycled}
                        </TableCell>
                    </TableRow>
                )
            })
        )
    };

    const See_Download_KoReport_Handler = (rpt: KoDataErasureReport) => {
        if (isT1aAdmin)
            return true;

        if (rpt && rpt.auths) {
            if (rpt.auths.see_Download_KoReport)
                return true;
        }
        return false;
    }

    return (
        <Box sx={style(matches)}>
            <Grid container spacing={2}
                alignItems="flex-start" 
            >
                {isT1aAdmin &&
                    <Grid item xs={12} md={4}>
                        <TableContainer component={Paper} >
                            <Table size="small" aria-label="Ko Modal">
                                <TableBody>
                                    <TableRow sx={{ bgcolor: '#478ac92e' }}>
                                        <TableCell align="left" sx={{ padding: 1, lineHeight: 1.6, color: '#000' }}>
                                            {reportData.pickupNumber}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow sx={{ bgcolor: '#478ac92e' }}>
                                        <TableCell scope="row" sx={tableCellStyle(matches)}>
                                            {reportData.partner_}<br />
                                            {reportData.customerCity}<br />
                                            {reportData.customerCountry}
                                        </TableCell>
                                    </TableRow>
                                    {leftColumn.map((column) => (
                                        <TableRow key={column.name1} sx={{ bgcolor: '#478ac92e' }}>
                                            <TableCell scope="row" sx={tableCellStyle(matches)}>
                                                {column.name1}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>
                }
                <Grid item xs={12} md={(isT1aAdmin) ? 8 : 12}>
                    <TableContainer component={Paper}>
                        <Table size="small" aria-label="a dense table">
                            <TableHead>
                                {generateKoReportingTableHeading()}
                            </TableHead>
                            <TableBody>
                                {generateKoReportingTableRows()}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <ItemChart sx={{ minHeight: matches ? 350 : 135 }}>
                        {reportData.received.laptops < 1 && reportData.received.desktops < 1
                            ?
                            <Typography color={'red'}>Enough data is not available to show on Pie Chart</Typography>
                            :
                            <KoReportingChart
                                data={getDataCaringUnitData([percentageHandler(true, false), percentageHandler(false, true) ?? 1], ['Upcycle', 'Recycle'])} />
                        }
                    </ItemChart>
                    <Stack
                        direction="row"
                        justifyContent="center"
                        alignItems="center"
                        spacing={2}
                        pt={2}
                    >
                        {!excelLoading ?
                            <>
                                {(See_Download_KoReport_Handler(reportData)) &&
                                    <>
                                        <Button size={matches ? "medium" : "small"} variant="contained"
                                            startIcon={<CloudDownloadOutlinedIcon />}
                                            onClick={() => downloadTemplate(reportData.pickupNumber)}
                                        >Report</Button>
                                        {/* <Button size={matches ? "medium" : "small"} variant="contained"

                                            startIcon={<CloudDownloadOutlinedIcon />}
                                            disabled={excelLoading || (percentageHandler(true, false) < 1 &&
                                                percentageHandler(false, true) < 1)}>Invoice</Button> */}
                                    </>
                                }
                                <Button size={matches ? "medium" : "small"} variant="contained" onClick={() => closeModal()}
                                    startIcon={<CloseIcon />}>Close</Button>
                            </>
                            :
                            <CircularProgress size='2.4rem' sx={{ color: '#234f1e' }} />
                        }
                    </Stack>
                </Grid>
            </Grid>
        </Box>
    );
}
export default observer(KoModal);